/**
 * koreanCompanyName — 미국 인기 티커의 한글 표기 매핑
 *
 * 사용자 결정 (#5): 대시보드 위젯에서 기업명 우선 표기 + 한국 이름이 가장 편함.
 *   AAPL → 애플 / TSLA → 테슬라 / NVDA → 엔비디아 …
 *
 * 매핑 없는 티커는 cleanCompanyName(data.name) 결과를 fallback.
 * KR 티커(6자리 숫자)는 이미 data.name 이 한글 (예: 삼성전자) — 매핑 불필요.
 */

import { cleanCompanyName } from "./clean-company-name";
// 자동 build: scripts/build-korean-names.js — Naver 금융 search API 일괄 fetch.
// NASDAQ + NYSE 6600+ ticker 의 한글 이름 매핑.
import naverNames from "@/data/korean-names.json";

const NAVER_NAMES = naverNames as Record<string, string>;

const TICKER_TO_KOREAN: Record<string, string> = {
  // Big Tech
  AAPL: "애플",
  MSFT: "마이크로소프트",
  GOOGL: "알파벳",
  GOOG: "알파벳",
  AMZN: "아마존",
  META: "메타",
  NFLX: "넷플릭스",
  TSLA: "테슬라",
  NVDA: "엔비디아",
  AMD: "AMD",
  INTC: "인텔",
  ORCL: "오라클",
  CRM: "세일즈포스",
  ADBE: "어도비",
  QCOM: "퀄컴",
  AVGO: "브로드컴",
  TXN: "텍사스 인스트루먼츠",
  // 반도체 / 하드웨어
  TSM: "TSMC",
  ASML: "ASML",
  AMAT: "어플라이드 머티리얼즈",
  LRCX: "램 리서치",
  KLAC: "KLA",
  MU: "마이크론",
  ARM: "ARM",
  // 자동차
  F: "포드",
  GM: "GM",
  TM: "토요타",
  HMC: "혼다",
  STLA: "스텔란티스",
  RIVN: "리비안",
  LCID: "루시드",
  // 금융
  JPM: "JP모건",
  BAC: "뱅크오브아메리카",
  WFC: "웰스파고",
  GS: "골드만삭스",
  MS: "모건스탠리",
  C: "씨티그룹",
  V: "비자",
  MA: "마스터카드",
  AXP: "아메리칸 익스프레스",
  PYPL: "페이팔",
  COIN: "코인베이스",
  SQ: "블록",
  // 헬스케어
  JNJ: "존슨앤존슨",
  PFE: "화이자",
  MRK: "머크",
  LLY: "일라이 릴리",
  ABBV: "애브비",
  UNH: "유나이티드헬스",
  TMO: "써모피셔",
  DHR: "다나허",
  MRNA: "모더나",
  // 소비재
  WMT: "월마트",
  COST: "코스트코",
  HD: "홈디포",
  NKE: "나이키",
  MCD: "맥도날드",
  KO: "코카콜라",
  PEP: "펩시코",
  PG: "P&G",
  SBUX: "스타벅스",
  DIS: "디즈니",
  // 미디어 / 통신
  T: "AT&T",
  VZ: "버라이즌",
  CMCSA: "컴캐스트",
  TMUS: "T모바일",
  // 항공우주 / 방산
  BA: "보잉",
  LMT: "록히드마틴",
  RTX: "RTX",
  NOC: "노스럽 그루먼",
  GD: "제너럴 다이내믹스",
  // 에너지
  XOM: "엑슨모빌",
  CVX: "셰브론",
  COP: "코노코필립스",
  SLB: "슐룸베르거",
  // 산업재
  CAT: "캐터필러",
  DE: "디어",
  GE: "GE",
  MMM: "3M",
  HON: "허니웰",
  // 인터넷 / 플랫폼
  UBER: "우버",
  LYFT: "리프트",
  ABNB: "에어비앤비",
  DASH: "도어대시",
  PINS: "핀터레스트",
  SNAP: "스냅",
  RBLX: "로블록스",
  SPOT: "스포티파이",
  ZM: "줌",
  // 차이나
  BABA: "알리바바",
  JD: "JD닷컴",
  PDD: "PDD홀딩스",
  BIDU: "바이두",
  NIO: "니오",
  XPEV: "샤오펑",
  LI: "리오토",
  // 위성 / 우주
  ASTS: "AST 스페이스모바일",
  RKLB: "로켓 랩",
  SPCE: "버진 갤럭틱",
  // 원자력 / 에너지 신산업
  SMR: "뉴스케일 파워",
  CCJ: "카메코",
  OKLO: "오클로",
  UEC: "유라늄에너지",
  LEU: "센트러스 에너지",
  URA: "우라늄 ETF",
  NXE: "NexGen 에너지",
  // 핀테크 / 디지털 자산
  SOFI: "소파이",
  HOOD: "로빈후드",
  AFRM: "어펌",
  UPST: "업스타트",
  RIOT: "라이엇 플랫폼",
  MARA: "마라톤 디지털",
  MSTR: "마이크로스트래티지",
  CLSK: "클린스파크",
  HUT: "허트8",
  WULF: "테라울프",
  // 데이터 / AI / SaaS
  PLTR: "팔란티어",
  SNOW: "스노우플레이크",
  DDOG: "데이터독",
  NET: "클라우드플레어",
  CRWD: "크라우드스트라이크",
  ZS: "지스케일러",
  PANW: "팔로알토",
  FTNT: "포티넷",
  OKTA: "옥타",
  MDB: "몽고DB",
  TEAM: "아틀라시안",
  WDAY: "워크데이",
  NOW: "서비스나우",
  // 콘텐츠 / 엔터테인먼트
  WBD: "워너브라더스 디스커버리",
  PARA: "파라마운트",
  EA: "일렉트로닉 아츠",
  TTWO: "테이크투",
  MTCH: "매치 그룹",
  // 소매 / 이커머스
  TGT: "타겟",
  LOW: "로우스",
  TJX: "TJX",
  EBAY: "이베이",
  ETSY: "엣시",
  W: "웨이페어",
  CHWY: "츄이",
  // 식품 / 음료
  MNST: "몬스터",
  SBI: "보스턴비어",
  HSY: "허쉬",
  KHC: "크래프트하인즈",
  GIS: "제너럴밀스",
  K: "켈로그",
  // 금융 — Berkshire 등
  "BRK.A": "버크셔 해서웨이",
  "BRK.B": "버크셔 해서웨이",
  BLK: "블랙록",
  SCHW: "찰스슈왑",
  ICE: "ICE",
  CME: "CME 그룹",
  CB: "처브",
  // 유틸리티
  NEE: "넥스트라 에너지",
  SO: "써던컴퍼니",
  DUK: "듀크에너지",
  // 광물 / 원자재
  CLF: "클리블랜드 클리프",
  X: "US 스틸",
  NUE: "뉴코어",
  FCX: "프리포트맥모란",
  RIO: "리오 틴토",
  BHP: "BHP",
  AA: "알코아",
  GOLD: "배릭골드",
  NEM: "뉴몬트",
  // 제약 / 바이오
  GILD: "길리어드",
  AMGN: "암젠",
  BMY: "BMS",
  CVS: "CVS 헬스",
  WBA: "월그린",
  HUM: "휴매나",
  CI: "시그나",
  ELV: "엘레번스 헬스",
  REGN: "리제네론",
  VRTX: "버텍스",
  BIIB: "바이오젠",
  ILMN: "일루미나",
  OGN: "오가논",
  PFGC: "퍼포먼스 푸드",
  // 산업 / 항공
  DAL: "델타항공",
  UAL: "유나이티드항공",
  AAL: "아메리칸항공",
  LUV: "사우스웨스트",
  FDX: "페덱스",
  UPS: "UPS",
  // 보험
  AIG: "AIG",
  PRU: "푸르덴셜",
  MET: "메트라이프",
  ALL: "올스테이트",
  TRV: "트래블러스",
  PGR: "프로그레시브",
  // 부동산 / 리츠
  AMT: "아메리칸 타워",
  PLD: "프롤로지스",
  CCI: "크라운 캐슬",
  EQIX: "에퀴닉스",
  SPG: "사이먼 프로퍼티",
  O: "리얼티 인컴",
  // 신흥 기술 / 그 외
  DOCU: "도큐사인",
  TWLO: "트윌리오",
  UPWK: "업워크",
  Z: "질로우",
  RDDT: "레딧",
  APP: "앱러빈",
  HIMS: "힘스",
  CELH: "셀시우스",
  ELF: "엘프 뷰티",
  ON: "온세미",
  WOLF: "울프스피드",
  ENPH: "엔페이즈",
  FSLR: "퍼스트솔라",
  RUN: "선런",
  PLUG: "플러그파워",
  BE: "블룸에너지",
  CHPT: "차지포인트",
  // 한국 ETF / KRX (혹시 6자리 외 매핑 필요 시)
  EWY: "MSCI 한국 ETF",
  // ETF (자주 보는 것만)
  SPY: "S&P 500 ETF",
  QQQ: "나스닥 100 ETF",
  IWM: "러셀 2000 ETF",
  DIA: "다우 ETF",
  VOO: "뱅가드 S&P 500",
  VTI: "뱅가드 토탈 마켓",
  // 지수 (시장 위젯)
  "^GSPC": "S&P 500",
  "^DJI": "다우 존스",
  "^IXIC": "나스닥",
  "^KS11": "코스피",
  "^KQ11": "코스닥",
};

/**
 * 한글 표기 우선 — 매핑 없으면 cleanCompanyName(원본) fallback.
 * KR 6자리 티커는 원본이 이미 한글이므로 cleanCompanyName 만 호출.
 */
export function koreanCompanyName(ticker: string | null | undefined, originalName: string | null | undefined): string {
  if (!ticker) return cleanCompanyName(originalName);
  const upper = String(ticker).toUpperCase().trim();
  // 1순위: 큐레이션 매핑 (인기 티커 — TICKER_TO_KOREAN)
  if (TICKER_TO_KOREAN[upper]) return TICKER_TO_KOREAN[upper];
  // 2순위: Naver 금융 자동 fetch 캐시 (NASDAQ+NYSE 6600+ ticker)
  if (NAVER_NAMES[upper]) return NAVER_NAMES[upper];
  // 3순위: KR 6자리 — 원본 이름이 이미 한글
  if (/^\d{6}$/.test(upper)) return cleanCompanyName(originalName) || upper;
  // 4순위: 영문 정리 후 반환
  return cleanCompanyName(originalName) || upper;
}
